import { useMemo, type ReactNode } from 'react';
import { Avatar, Dropdown, Layout, Menu, Space, Tag, Typography } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  LogoutOutlined,
  BankOutlined,
  SettingOutlined,
  SendOutlined,
  FileDoneOutlined,
  LinkOutlined,
  ImportOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ROLE_COLOR, ROLE_LABEL, isAdminOrFinance, normalizeRoles } from '@/domain/roles';
import { useSessionStore } from '@/store/session';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

/**
 * 顶栏高度。左上角的 hiPay 和右侧顶栏**共用这一个值**，两边的底边线才能连成一条 ——
 * 各写各的（antd Header 默认 64、logo 区写 56）会让左右差出 8px，侧栏菜单比内容区高一截。
 * 底边线也要两边都画，否则那条线会在侧栏处断开。
 */
const HEADER_HEIGHT = 56;
const HEADER_BORDER = '1px solid #f0f0f0';

/**
 * 全站骨架：左侧导航 + 顶栏（组织名 / 当前身份 / 退出）。业务页面由 <Outlet /> 渲染，
 * 路由表在 src/router.tsx。新增一级页面：router.tsx 加 route，再往下面 NAV 加一项，
 * **并想清楚它的 gate**（不写就是所有角色可见）。
 *
 * 🔴 `gate` 只是**不显示一个必然 403 的入口**，不是权限。同一条判定在 router.tsx 上
 * 再挡一次（直接敲 URL 的人），真正的把关在服务端。三处都改，少一处就漏。
 */
type Gate = 'adminOrFinance' | 'admin';

const NAV: { key: string; icon: ReactNode; label: ReactNode; gate?: Gate }[] = [
  // 概览页兼「我的资料」（姓名 / 邮箱 / 角色，全部只读），所以没有单独的资料入口
  { key: '/', icon: <DashboardOutlined />, label: <Link to="/">概览</Link> },
  // 顺序按业务链路来：先把收款信息收上来 → 落成收款人 → 提申请 → 看记录。
  // 收集链接是收款人的上游入口（填上来的就变成一个草稿收款人），排在它前面。
  //
  // 概览 / 收款信息收集 / 收款人 这三项没有 gate：member 能看到的就是这三页 ——
  // 收信息、落成收款人，到此为止。从「批量导入」开始都是资金链路，member 一律看不到。
  {
    key: '/collection-links',
    icon: <LinkOutlined />,
    label: <Link to="/collection-links">收款信息收集</Link>,
  },
  { key: '/payees', icon: <BankOutlined />, label: <Link to="/payees">收款人</Link> },
  // 排在收款人与打款申请之间：它一次同时产出这两样东西，位置正对应它在链路上的跨度。
  // ⚠️ gate 必须是 adminOrFinance —— 这一页能创建打款申请，member 碰不得。
  {
    key: '/payout-batch-import',
    icon: <ImportOutlined />,
    label: <Link to="/payout-batch-import">批量导入</Link>,
    gate: 'adminOrFinance',
  },
  {
    key: '/payout-applications/ai-batch',
    icon: <ThunderboltOutlined />,
    label: <Link to="/payout-applications/ai-batch">AI 批量创建申请</Link>,
    gate: 'adminOrFinance',
  },
  {
    key: '/payout-applications',
    icon: <SendOutlined />,
    label: <Link to="/payout-applications">打款申请</Link>,
    gate: 'adminOrFinance',
  },
  {
    key: '/payouts',
    icon: <FileDoneOutlined />,
    label: <Link to="/payouts">打款记录</Link>,
    gate: 'adminOrFinance',
  },
  {
    key: '/members',
    icon: <TeamOutlined />,
    label: <Link to="/members">成员</Link>,
    gate: 'adminOrFinance',
  },
  { key: '/settings', icon: <SettingOutlined />, label: <Link to="/settings">设置</Link>, gate: 'admin' },
];

export default function AppLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const me = useSessionStore((s) => s.me);
  const signOut = useSessionStore((s) => s.signOut);

  const roles = me?.roles ?? [];
  const isAdmin = roles.includes('admin');
  const adminOrFinance = isAdminOrFinance(roles);
  const navItems = useMemo(
    () =>
      NAV.filter((n) => {
        if (n.gate === 'admin') return isAdmin;
        if (n.gate === 'adminOrFinance') return adminOrFinance;
        return true;
      }).map(({ gate: _gate, ...item }) => item),
    [isAdmin, adminOrFinance],
  );

  // 选中项按「最长前缀匹配」算，这样 /members/123 这类子路由也能点亮父级菜单；
  // 直接用 pathname 当 selectedKeys 会让进详情页后整个导航失去高亮。
  // 这里用全量 NAV 而不是 navItems：命中一个自己看不到的页面时，宁可整条导航不高亮，
  // 也不要错点亮一个前缀更短的邻居。
  const selectedKey = useMemo(() => {
    const hit = NAV.map((n) => n.key)
      .filter((k) => k !== '/' && pathname.startsWith(k))
      .sort((a, b) => b.length - a.length)[0];
    return hit ?? '/';
  }, [pathname]);

  function logout() {
    signOut();
    navigate('/login', { replace: true });
  }

  return (
    // 整个应用壳固定在视口内，禁止 body 参与滚动；只有右侧 Content 是滚动容器。
    // 用动态视口高度，移动端浏览器地址栏改变可视区域时也不会留下多余的一截。
    <Layout style={{ height: '100dvh', overflow: 'hidden' }}>
      <Sider breakpoint="lg" collapsedWidth={64} theme="light" style={{ height: '100%' }}>
        <div
          style={{
            height: HEADER_HEIGHT,
            borderBottom: HEADER_BORDER,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: 18,
          }}
        >
          hiPay
        </div>
        <Menu mode="inline" selectedKeys={[selectedKey]} items={navItems} />
      </Sider>
      {/* minHeight: 0 是 flex 子项允许内部收缩并产生滚动条的关键；没有它时，长页面会把
          整个 Layout 撑高，最终仍然变成 body 滚动。 */}
      <Layout style={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
        <Header
          style={{
            flex: '0 0 auto',
            height: HEADER_HEIGHT,
            lineHeight: `${HEADER_HEIGHT}px`,
            background: '#fff',
            paddingInline: 16,
            borderBottom: HEADER_BORDER,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Text strong>{me?.organization.name}</Text>
          {me && (
            <Dropdown
              menu={{
                items: [
                  { key: 'profile', icon: <UserOutlined />, label: <Link to="/">我的资料</Link> },
                  { type: 'divider' },
                  { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: logout },
                ],
              }}
            >
              {/* 角色在左、头像与名字在最右：一眼要找的是「我是谁」，
                  它固定贴在屏幕右缘；角色是补充信息，往左让位。 */}
              <Space style={{ cursor: 'pointer' }}>
                {/* 角色之间挨紧一点，与右边的头像分成两组，不然连成一长串读不出层次 */}
                <Space size={4}>
                  {normalizeRoles(me.roles).map((r) => (
                    <Tag key={r} color={ROLE_COLOR[r]} style={{ marginInlineEnd: 0 }}>
                      {ROLE_LABEL[r]}
                    </Tag>
                  ))}
                </Space>
                <Avatar size="small" icon={<UserOutlined />} />
                <span>{me.user.name}</span>
              </Space>
            </Dropdown>
          )}
        </Header>
        {/* 内容区留白比 antd 默认的 24 小一档：表格页在 1280 宽的屏幕上很吃横向空间，
            外层每少 8px，打款记录那种十来列的表格就少一分横向滚动 */}
        <Content style={{ minHeight: 0, overflow: 'auto', padding: 16 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
